import { useState, useEffect, useCallback } from 'react';
import { fetchDesktopDownloads, DesktopDownloads } from '@/services/desktopDownloadService';
import { fetchDesktopFeedVersions, DesktopFeedVersions } from '@/services/desktopFeedService';

/**
 * Hook to fetch desktop app download links from Firebase, plus the version each
 * build's update feed is currently publishing.
 */
export const useDesktopDownloads = () => {
  const [downloads, setDownloads] = useState<DesktopDownloads | null>(null);
  const [feedVersions, setFeedVersions] = useState<DesktopFeedVersions>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    let cancelled = false;

    const loadDownloads = async () => {
      setLoading(true);
      setError(null);

      // Neither call throws; the feed read is capped so it can't hold the page.
      const [config, versions] = await Promise.all([fetchDesktopDownloads(), fetchDesktopFeedVersions()]);
      if (cancelled) {
        return;
      }

      setFeedVersions(versions);
      if (config) {
        setDownloads(config);
      } else {
        setError('No desktop download links found');
      }
      setLoading(false);
    };

    loadDownloads();

    return () => {
      cancelled = true;
    };
  }, [revision]);

  return { downloads, feedVersions, loading, error, refresh };
};
